import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import {
    Eye,
    EyeOff,
    Loader2,
    Mail,
    User,
    Sparkles,
    Shield,
    Zap,
    ChevronRight
} from "lucide-react";

import api from '../../api/axios';

const SignupPage = () => {
    const navigate = useNavigate();
    const [mounted, setMounted] = useState(false);

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        user_name: "",
        email: "",
        mobile: "",
        password: "",
        confirmPassword: "",
        role: "admin"
    });

    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [focusedField, setFocusedField] = useState(null);

    const { success, error } = useToast();

    useEffect(() => {
        setMounted(true);
    }, []);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        setErrors({
            ...errors,
            [e.target.name]: "",
        });
    };

    const validate = () => {
        let newErrors = {};

        if (!formData.user_name.trim()) {
            newErrors.user_name = "Full name is required";
        } else if (formData.user_name.trim().length < 2) {
            newErrors.user_name = "Name must be at least 2 characters";
        }

        if (!formData.email) {
            newErrors.email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "Enter a valid email address";
        }

        if (!formData.mobile) {
            newErrors.mobile = "Mobile number is required";
        } else if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
            newErrors.mobile = "Enter a valid mobile number";
        }

        if (!formData.password) {
            newErrors.password = "Password is required";
        } else if (formData.password.length < 8) {
            newErrors.password = "Password must be at least 8 characters";
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
            newErrors.password = "Must include uppercase, lowercase & number";
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = "Confirm your password";
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match";
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            setLoading(true);
            const response = await api.post("/auth/register", formData);
            if (response && response.data && response.data.success) {
                setLoading(false);
                success("Account created successfully! 🎉 Please sign in.");
                navigate("/sign-in");
            } else {
                setLoading(false);
                success(response.data.message);
            }
        } catch (err) {
            setLoading(false);
            console.log(err?.data?.message);
            error(err?.data?.message);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4 relative overflow-hidden">

            {/* Soft Background Elements */}
            {/* <div className="absolute inset-0 overflow-hidden">
                <div className={`absolute w-[800px] h-[800px] bg-indigo-200/30 rounded-full blur-[120px] top-[-400px] left-[-200px] transition-all duration-1000 ${mounted ? 'opacity-100' : 'opacity-0'}`}></div>
                <div className={`absolute w-[600px] h-[600px] bg-purple-200/30 rounded-full blur-[120px] bottom-[-300px] right-[-100px] transition-all duration-1000 delay-300 ${mounted ? 'opacity-100' : 'opacity-0'}`}></div>
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:40px_40px]"></div>
            </div> */}

            <div className="absolute inset-0 overflow-hidden">
                <div className={`absolute w-[800px] h-[800px] rounded-full bg-indigo-300/20 blur-[160px] top-[-500px] left-[-300px] transition-all duration-1000 ${mounted ? 'opacity-100' : 'opacity-0'}`}></div>
                <div className={`absolute w-[800px] h-[800px] rounded-full bg-blue-300/20 blur-[150px] bottom-[-400px] right-[-250px] transition-all duration-1000 delay-300 ${mounted ? 'opacity-100' : 'opacity-0'}`}></div>
                <div className={`absolute w-[700px] h-[700px] rounded-full bg-violet-300/20 blur-[140px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-1000 delay-500 ${mounted ? 'opacity-100' : 'opacity-0'}`}></div>
                <div className={`absolute w-[500px] h-[500px] rounded-full bg-pink-200/25 blur-[120px] top-1/4 right-1/4 transition-all duration-1000 delay-700 ${mounted ? 'opacity-100' : 'opacity-0'}`}></div>
            </div>

            {/* Main Card */}
            <div className={`w-full max-w-5xl relative transition-all duration-1000 transform ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-200">
                    <div className="grid md:grid-cols-2">

                        {/* Left Side - Branding & Benefits */}
                        <div className="relative p-8 md:p-12 bg-gradient-to-br from-indigo-50 to-purple-50 flex flex-col">

                            {/* Logo */}
                            <div className="flex items-center gap-3 mb-8">
                                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center shadow-sm">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="m17 2-5 5-5-5"></path>
                                        <rect width="20" height="15" x="2" y="7" rx="2"></rect>
                                    </svg>
                                </div>
                                <h1 className="text-2xl font-semibold text-gray-800">
                                    SocialTV
                                </h1>
                            </div>

                            {/* Hero Text */}
                            <div className="relative z-10">
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 leading-tight">
                                    Start creating<br />
                                    <span className="text-indigo-600">
                                        amazing TV ads
                                    </span>
                                </h2>
                                <p className="text-gray-600 mb-8 leading-relaxed">
                                    Join thousands of creators who are transforming their social content into stunning 4K television advertisements.
                                </p>

                                {/* Benefits List */}
                                <div className="space-y-4">
                                    {[
                                        { icon: Zap, text: "Free 14-day trial", desc: "No credit card required" },
                                        { icon: Sparkles, text: "4K quality exports", desc: "Broadcast-ready output" },
                                        { icon: Shield, text: "Cancel anytime", desc: "No contracts or hidden fees" }
                                    ].map((benefit, index) => (
                                        <div key={index} className="flex gap-3">
                                            <div className="flex-shrink-0">
                                                <benefit.icon size={18} className="text-indigo-500 mt-1" />
                                            </div>
                                            <div>
                                                <h3 className="font-medium text-gray-800">{benefit.text}</h3>
                                                <p className="text-sm text-gray-500">{benefit.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Trust Badge */}
                            <div className="mt-8 pt-6 border-t border-indigo-100">
                                <p className="text-xs text-gray-500 flex items-center gap-2">
                                    <span className="flex -space-x-3">
                                        {[1, 2, 3, 4].map((i) => (
                                            <div key={i} className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 border-2 border-white"></div>
                                        ))}
                                    </span>
                                    <span>Trusted by 10,000+ creators worldwide</span>
                                </p>
                            </div>
                        </div>

                        {/* Right Side - Signup Form */}
                        <div className="p-8 md:p-12 bg-white">
                            {/* Form Header */}
                            <div className="mb-6">
                                <h3 className="text-2xl font-semibold text-gray-800 mb-1">
                                    Create account
                                </h3>
                                <p className="text-gray-500 text-sm">
                                    Get started with your free trial today
                                </p>
                            </div>

                            {/* Form */}
                            <form className="space-y-4" onSubmit={handleSubmit}>
                                {/* Name */}
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Full Name
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="user_name"
                                            value={formData.user_name}
                                            onChange={handleChange}
                                            onFocus={() => setFocusedField('user_name')}
                                            onBlur={() => setFocusedField(null)}
                                            placeholder="John Doe"
                                            className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 pl-10
                                                ${errors.user_name
                                                    ? "border-red-300 bg-red-50/50"
                                                    : focusedField === 'user_name'
                                                        ? "border-indigo-300 bg-indigo-50/30"
                                                        : "border-gray-200 bg-gray-50"
                                                }
                                                outline-none focus:ring-1 
                                                ${errors.user_name
                                                    ? "focus:ring-red-100"
                                                    : "focus:ring-indigo-100"
                                                }
                                                text-sm`}
                                        />
                                        <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors
                                            ${errors.user_name
                                                ? "text-red-400"
                                                : focusedField === 'user_name'
                                                    ? "text-indigo-400"
                                                    : "text-gray-400"
                                            }`}
                                        />
                                    </div>
                                    {errors.user_name && (
                                        <p className="text-red-500 text-xs mt-1">
                                            {errors.user_name}
                                        </p>
                                    )}
                                </div>

                                {/* Email */}
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Email
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            onFocus={() => setFocusedField('email')}
                                            onBlur={() => setFocusedField(null)}
                                            placeholder="you@example.com"
                                            className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 pl-10
                                                ${errors.email
                                                    ? "border-red-300 bg-red-50/50"
                                                    : focusedField === 'email'
                                                        ? "border-indigo-300 bg-indigo-50/30"
                                                        : "border-gray-200 bg-gray-50"
                                                }
                                                outline-none focus:ring-1 
                                                ${errors.email
                                                    ? "focus:ring-red-100"
                                                    : "focus:ring-indigo-100"
                                                }
                                                text-sm`}
                                        />
                                        <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors
                                            ${errors.email
                                                ? "text-red-400"
                                                : focusedField === 'email'
                                                    ? "text-indigo-400"
                                                    : "text-gray-400"
                                            }`}
                                        />
                                    </div>
                                    {errors.email && (
                                        <p className="text-red-500 text-xs mt-1">
                                            {errors.email}
                                        </p>
                                    )}
                                </div>

                                {/* Mobile */}
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Mobile
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            name="mobile"
                                            value={formData.mobile}
                                            onChange={handleChange}
                                            onFocus={() => setFocusedField('mobile')}
                                            onBlur={() => setFocusedField(null)}
                                            placeholder="999-999-9999"
                                            className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 pl-10
                                                ${errors.mobile
                                                    ? "border-red-300 bg-red-50/50"
                                                    : focusedField === 'mobile'
                                                        ? "border-indigo-300 bg-indigo-50/30"
                                                        : "border-gray-200 bg-gray-50"
                                                }
                                                outline-none focus:ring-1 
                                                ${errors.mobile
                                                    ? "focus:ring-red-100"
                                                    : "focus:ring-indigo-100"
                                                }
                                                text-sm`}
                                        />
                                        <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors
                                            ${errors.mobile
                                                ? "text-red-400"
                                                : focusedField === 'mobile'
                                                    ? "text-indigo-400"
                                                    : "text-gray-400"
                                            }`}
                                        />
                                    </div>
                                    {errors.mobile && (
                                        <p className="text-red-500 text-xs mt-1">
                                            {errors.mobile}
                                        </p>
                                    )}
                                </div>

                                {/* Password */}
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            onFocus={() => setFocusedField('password')}
                                            onBlur={() => setFocusedField(null)}
                                            placeholder="••••••••"
                                            className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 pl-10 pr-10
                                                ${errors.password
                                                    ? "border-red-300 bg-red-50/50"
                                                    : focusedField === 'password'
                                                        ? "border-indigo-300 bg-indigo-50/30"
                                                        : "border-gray-200 bg-gray-50"
                                                }
                                                outline-none focus:ring-1 
                                                ${errors.password
                                                    ? "focus:ring-red-100"
                                                    : "focus:ring-indigo-100"
                                                }
                                                text-sm`}
                                        />
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                                                className={`transition-colors ${errors.password
                                                    ? "text-red-400"
                                                    : focusedField === 'password'
                                                        ? "text-indigo-400"
                                                        : "text-gray-400"
                                                    }`}>
                                                <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" />
                                            </svg>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors
                                                ${focusedField === 'password'
                                                    ? "text-indigo-400"
                                                    : "text-gray-400 hover:text-gray-600"
                                                }`}
                                        >
                                            {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                                        </button>
                                    </div>
                                    {errors.password && (
                                        <p className="text-red-500 text-xs mt-1">
                                            {errors.password}
                                        </p>
                                    )}
                                    {/* {!errors.password && formData.password && (
                                        <div className="flex gap-1 mt-1">
                                            {[
                                                formData.password.length >= 8,
                                                /[a-z]/.test(formData.password),
                                                /[A-Z]/.test(formData.password),
                                                /\d/.test(formData.password)
                                            ].map((isValid, i) => (
                                                <div key={i}
                                                    className={`h-1 flex-1 rounded-full transition-colors ${isValid ? 'bg-green-400' : 'bg-gray-200'
                                                        }`}
                                                />
                                            ))}
                                        </div>
                                    )} */}
                                </div>

                                {/* Confirm Password */}
                                <div className="space-y-1.5">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Confirm Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            onFocus={() => setFocusedField('confirmPassword')}
                                            onBlur={() => setFocusedField(null)}
                                            placeholder="••••••••"
                                            className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-200 pl-10 pr-10
                                                ${errors.confirmPassword
                                                    ? "border-red-300 bg-red-50/50"
                                                    : focusedField === 'confirmPassword'
                                                        ? "border-indigo-300 bg-indigo-50/30"
                                                        : "border-gray-200 bg-gray-50"
                                                }
                                                outline-none focus:ring-1 
                                                ${errors.confirmPassword
                                                    ? "focus:ring-red-100"
                                                    : "focus:ring-indigo-100"
                                                }
                                                text-sm`}
                                        />
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                                                className={`transition-colors ${errors.confirmPassword
                                                    ? "text-red-400"
                                                    : focusedField === 'confirmPassword'
                                                        ? "text-indigo-400"
                                                        : "text-gray-400"
                                                    }`}>
                                                <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" />
                                                <path d="M12 15v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors
                                                ${focusedField === 'confirmPassword'
                                                    ? "text-indigo-400"
                                                    : "text-gray-400 hover:text-gray-600"
                                                }`}
                                        >
                                            {showConfirmPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                                        </button>
                                    </div>
                                    {errors.confirmPassword && (
                                        <p className="text-red-500 text-xs mt-1">
                                            {errors.confirmPassword}
                                        </p>
                                    )}
                                    {!errors.confirmPassword && formData.confirmPassword && formData.password === formData.confirmPassword && (
                                        <p className="text-green-500 text-xs mt-1 flex items-center gap-1">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                <path d="M20 6L9 17l-5-5" />
                                            </svg>
                                            Passwords match
                                        </p>
                                    )}
                                </div>

                                {/* Terms */}
                                <div className="flex items-center gap-3 !mt-6">
                                    <input
                                        type="checkbox"
                                        id="terms"
                                        className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-200"
                                        required
                                    />
                                    <label htmlFor="terms" className="text-xs text-gray-500">
                                        I agree to the{" "}
                                        <Link to={'#'} className="text-indigo-600 hover:underline">Terms of Service</Link>
                                        {" "}and{" "}
                                        <Link to={"#"} className="text-indigo-600 hover:underline">Privacy Policy</Link>
                                    </label>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full flex items-center justify-center gap-2 bg-indigo-600 text-white py-2.5 rounded-lg hover:bg-indigo-700 transition-all font-medium text-sm disabled:opacity-70 disabled:cursor-not-allowed mt-6"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="animate-spin" size={16} />
                                            Creating account...
                                        </>
                                    ) : (
                                        <>
                                            <span>Create Account</span>
                                            <ChevronRight size={16} />
                                        </>
                                    )}
                                </button>

                                {/* Sign In Link */}
                                <p className="text-center text-xs text-gray-500 mt-4">
                                    Already have an account?{" "}
                                    <button
                                        type="button"
                                        onClick={() => navigate("/sign-in")}
                                        className="font-medium text-indigo-600 hover:text-indigo-700 hover:underline"
                                    >
                                        Sign in
                                    </button>
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignupPage;